import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface TrialEligibility {
  eligible: boolean;
  hasUsedTrial: boolean;
  reason?: string;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to check if user is eligible for free trial
 * Follows best practices: checks Stripe subscription history
 */
export const useTrialEligibility = (): TrialEligibility => {
  const { user, session } = useAuth();
  const [eligibility, setEligibility] = useState<TrialEligibility>({
    eligible: true, // Default to eligible to avoid blocking users
    hasUsedTrial: false,
    loading: true,
    error: null,
  });

  const checkEligibility = useCallback(async () => {
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      setEligibility({
        eligible: true,
        hasUsedTrial: false,
        loading: false,
        error: null,
      });
      return;
    }

    try {
      setEligibility(prev => ({ ...prev, loading: true, error: null }));

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        setEligibility({
          eligible: true,
          hasUsedTrial: false,
          loading: false,
          error: 'No access token',
        });
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-trial-eligibility`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // ✅ Log l'erreur pour debugging mais default à eligible pour ne pas bloquer l'utilisateur
        console.warn('⚠️ Trial eligibility check failed:', {
          status: response.status,
          error: errorData.error || 'Unknown error',
          user_id: user.id
        });
        // Default à eligible pour ne pas bloquer l'utilisateur, mais log l'erreur
        setEligibility({
          eligible: true, // ✅ Default à eligible pour UX, mais log l'erreur
          hasUsedTrial: false,
          loading: false,
          error: errorData.error || 'Failed to check eligibility',
        });
        return;
      }

      const data = await response.json();
      setEligibility({
        eligible: data.eligible ?? true,
        hasUsedTrial: data.hasUsedTrial ?? false,
        reason: data.reason,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.warn('Error checking trial eligibility (non-critical):', error);
      // Default to eligible on error to avoid blocking users
      setEligibility({
        eligible: true,
        hasUsedTrial: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [user?.id, session?.access_token]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  return eligibility;
};

